import { useLayoutEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom-v5-compat'

import { useAuthenticatedUser } from '@sparkai/auth'
import services, { getHttpStatus, shouldRetryShareLinkResolution } from '@sparkai/services/spark'
import { UserRoles } from '@sparkai/shared-types'

import { ShareLinkAccessState } from './ShareLinkAccessState'
import {
  buildAdminTaskDestination,
  buildAppUrl,
  buildNoAccessDestination,
  buildWfmTaskDestination,
  getShareLinkTargetApp,
  TASK_NOT_FOUND_RESULT,
} from './shareLinkResolution'

type TaskEngagementLink = {
  engagementId: string
  taskId: string
}

type TaskReviewLink = {
  taskId: string
  taskReviewId: string
}

type ShareLinkTargetApp = ReturnType<typeof getShareLinkTargetApp>

type DestinationBuilder = () => string

type TaskResolverParams = {
  taskEngagements?: TaskEngagementLink[]
  taskId: string
  taskReviews?: TaskReviewLink[]
  search: string
  hash: string
}

const canRedirectToTargetApp = (targetApp: ShareLinkTargetApp) => targetApp !== 'unsupported'
const shouldRedirectNotFound = (status: number | undefined) => status === 400 || status === 404
const shouldRedirectNoAccess = (status: number | undefined) => status === 401 || status === 403

const resolveAdminTaskLink = ({ taskEngagements, taskId, search, hash }: TaskResolverParams) => {
  const taskEngagement = taskEngagements?.find((item) => item.taskId === taskId)
  if (!taskEngagement) return taskEngagements ? TASK_NOT_FOUND_RESULT : undefined

  const query = search ? search.replace('?', '&') : ''
  return {
    outcome: 'allowed' as const,
    destinationUrl: buildAdminTaskDestination(taskEngagement.engagementId, taskId) + query + hash,
  }
}

const resolveCustomerTaskLink = ({ taskEngagements, taskId, search, hash }: TaskResolverParams) => {
  const taskEngagement = taskEngagements?.find((item) => item.taskId === taskId)
  if (!taskEngagement) return taskEngagements ? TASK_NOT_FOUND_RESULT : undefined

  return {
    outcome: 'allowed' as const,
    destinationUrl: buildAppUrl('/customer/', `engagement/${taskEngagement.engagementId}${search}${hash}`),
  }
}

const resolveMissionSpecialistTaskLink = ({ taskReviews, taskId, search, hash }: TaskResolverParams) => {
  const taskReview = taskReviews?.find((item) => item.taskId === taskId)
  if (!taskReview) return taskReviews ? TASK_NOT_FOUND_RESULT : undefined

  return {
    outcome: 'allowed' as const,
    destinationUrl: buildAppUrl('/ms/', `task-review/${taskReview.taskReviewId}${search}${hash}`),
  }
}

const resolveWorkforceManagerTaskLink = ({ taskId, search, hash }: TaskResolverParams) => {
  return {
    outcome: 'allowed' as const,
    destinationUrl: buildWfmTaskDestination(taskId) + search + hash,
  }
}

type TaskResolution =
  | ReturnType<typeof resolveAdminTaskLink>
  | ReturnType<typeof resolveCustomerTaskLink>
  | ReturnType<typeof resolveMissionSpecialistTaskLink>
  | ReturnType<typeof resolveWorkforceManagerTaskLink>

const taskResolvers: Partial<Record<ShareLinkTargetApp, (params: TaskResolverParams) => TaskResolution>> = {
  admin: resolveAdminTaskLink,
  customer: resolveCustomerTaskLink,
  'mission-specialist': resolveMissionSpecialistTaskLink,
  'workforce-manager': resolveWorkforceManagerTaskLink,
}

const taskNotFoundDestinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>> = {
  admin: () => buildNoAccessDestination('/admin/', 'no-access', 'not-found'),
  customer: () => buildNoAccessDestination('/customer/', 'no-access'),
  'mission-specialist': () => buildNoAccessDestination('/ms/', 'task-not-available', 'not-found'),
}

const taskUnavailableDestinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>> = {
  admin: () => buildNoAccessDestination('/admin/', 'task-not-available', 'not-found'),
  customer: () => buildNoAccessDestination('/customer/', 'engagement-not-available', 'not-found'),
  'mission-specialist': () => buildNoAccessDestination('/ms/', 'task-not-available', 'not-found'),
}

const taskNoAccessDestinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>> = {
  admin: () => buildNoAccessDestination('/admin/', 'no-access', 'not-found'),
  customer: () => buildNoAccessDestination('/customer/', 'no-access'),
  'mission-specialist': () => buildNoAccessDestination('/ms/', 'no-access'),
}

const resolveTaskLink = ({
  role,
  taskId,
  ...params
}: {
  role?: UserRoles
  taskEngagements?: TaskEngagementLink[]
  taskId?: string
  taskReviews?: TaskReviewLink[]
  search: string
  hash: string
}) => {
  if (!role || !taskId) return

  return taskResolvers[getShareLinkTargetApp(role)]?.({ ...params, taskId })
}

function getDestination(
  destinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>>,
  targetApp: ShareLinkTargetApp
) {
  return destinations[targetApp]?.()
}

function getTaskNotFoundDestination(targetApp: ShareLinkTargetApp) {
  return getDestination(taskNotFoundDestinations, targetApp)
}

function getTaskErrorDestination(status: number | undefined, targetApp: ShareLinkTargetApp) {
  if (shouldRedirectNotFound(status)) return getDestination(taskUnavailableDestinations, targetApp)
  if (shouldRedirectNoAccess(status)) return getDestination(taskNoAccessDestinations, targetApp)
}

function shouldWaitForTaskErrorRedirect(status: number | undefined, targetApp: ShareLinkTargetApp) {
  return (
    (shouldRedirectNotFound(status) || shouldRedirectNoAccess(status)) && canRedirectToTargetApp(targetApp)
  )
}

export function CanonicalTaskLink() {
  const { taskId } = useParams<'taskId'>()
  const location = useLocation()
  const { profile } = useAuthenticatedUser()
  const taskIds = useMemo(() => (taskId ? [taskId] : []), [taskId])
  const targetApp = getShareLinkTargetApp(profile?.role)
  const shouldLoadEngagement = targetApp === 'admin'
  const shouldLoadCustomerEngagement = targetApp === 'customer'
  const shouldLoadTaskReview = targetApp === 'mission-specialist'
  const {
    data: taskEngagements,
    error: taskEngagementError,
    isError: isTaskEngagementError,
    isLoading: isTaskEngagementLoading,
    refetch: refetchTaskEngagement,
  } = services.tasks.getTasksEngagement(taskIds, !shouldLoadEngagement && !shouldLoadCustomerEngagement, {
    retry: shouldRetryShareLinkResolution,
  })
  const {
    data: taskReviews,
    error: taskReviewError,
    isError: isTaskReviewError,
    isLoading: isTaskReviewLoading,
    refetch: refetchTaskReview,
  } = services.missionSpecialist.tasksReview.mineIds(taskIds, !shouldLoadTaskReview, {
    retry: shouldRetryShareLinkResolution,
  })

  const resolution = useMemo(() => {
    return resolveTaskLink({
      role: profile?.role,
      taskEngagements,
      taskId,
      taskReviews,
      search: location.search,
      hash: location.hash,
    })
  }, [profile, taskEngagements, taskId, taskReviews, location.search, location.hash])

  useLayoutEffect(() => {
    if (resolution?.outcome === 'allowed') {
      globalThis.location.href = resolution.destinationUrl
    }
    if (resolution?.outcome === 'not-found') {
      const destination = getTaskNotFoundDestination(targetApp)
      if (destination) globalThis.location.href = destination
    }
  }, [resolution, targetApp])

  useLayoutEffect(() => {
    if (!profile || (!isTaskEngagementError && !isTaskReviewError)) return

    const error = taskEngagementError ?? taskReviewError
    const destination = getTaskErrorDestination(getHttpStatus(error), targetApp)
    if (destination) globalThis.location.href = destination
  }, [isTaskEngagementError, isTaskReviewError, profile, targetApp, taskEngagementError, taskReviewError])

  const isLoading =
    !profile || isTaskEngagementLoading || isTaskReviewLoading || resolution?.outcome === 'allowed'

  if (isLoading) return <ShareLinkAccessState state='loading' />
  if (isTaskEngagementError || isTaskReviewError) {
    const error = taskEngagementError ?? taskReviewError
    const status = getHttpStatus(error)
    const onRetry = shouldLoadTaskReview ? refetchTaskReview : refetchTaskEngagement
    if (shouldWaitForTaskErrorRedirect(status, targetApp)) {
      return <ShareLinkAccessState state='loading' />
    }
    if (status === 400 || status === 404) {
      return <ShareLinkAccessState message={TASK_NOT_FOUND_RESULT.message} state='not-found' />
    }
    if (status === 401 || status === 403) {
      return <ShareLinkAccessState state='no-access' />
    }

    return <ShareLinkAccessState message={String(error)} onRetry={() => void onRetry()} state='error' />
  }
  if (!resolution) return <ShareLinkAccessState state='not-found' />
  if (resolution.outcome === 'not-found' && canRedirectToTargetApp(targetApp)) {
    return <ShareLinkAccessState state='loading' />
  }
  if (resolution.outcome === 'not-found')
    return <ShareLinkAccessState message={resolution.message} state='not-found' />

  return <ShareLinkAccessState state='loading' />
}
